

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
}



interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const multivitaminIv: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "multivitamin-iv-drips",
        "meta": {
            "title": "Multivitamin IV Drips in Dubai | Elite Body Home",
            "description": "Replenish essential vitamins and minerals directly into your bloodstream for immediate absorption and energy boost. Safe and effective treatment with no downtime.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/multivitamin-iv-drips-dubai/",
            "og": {
                "title": "Multivitamin IV Drips in Dubai | Elite Body Home",
                "description": "Direct infusion of essential vitamins and minerals for immediate health benefits",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-48.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Multivitamin IV Drips"],
        "hero": {
            "title": "Multivitamin IV Drips in Dubai",
            "subtitle": "Direct Vitamin Infusion Therapy",
            "description": "Many modern lifestyles and diets often result in micronutrient deficiencies. As vitamin and mineral levels are crucial for overall health, Elite Body Home Polyclinic, located in Dubai, understand this better than anyone. The Multivitamin IV Drips help you unwind as you re-nourish your body."
        },
        "howItWorks": {
            "title": "How Do Multivitamin IV Drips Work?",
            "description": "Our Multivitamin IV Drips: A personalized infusion of vital vitamins, minerals, and antioxidants directly into your bloodstream. This skips the digestive system, which means your body absorbs it all, and you will feel the effects immediately."
        },
        "procedureSteps": {
            "heading": "Key Components of Our IV Drips",
            "para": "Our specialized formula contains essential nutrients for optimal health:",
            "steps": [
                {
                    "title": "B-Complex Vitamins",
                    "desc": "Convert food nutrients into energy and promoting cognitive function",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Vitamin C",
                    "desc": "A strong antioxidant that enhances immunity & collagen production",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "Vitamin D",
                    "desc": "Important for Bone Density & Immune Function",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "Electrolytes",
                    "desc": "Help keep you hydrated and aid muscle function",
                    "icon": "fa-solid fa-droplet"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Multivitamin IV Therapy",
            "subheading": "Experience immediate and long-term health improvements",
            "para": "The most essential benefits of Multivitamin IV drips:",
            "items": [
                {
                    "title": "Nutrient Deficiency Correction",
                    "desc": "Curb vitamin and mineral deficiencies effectively",
                    "icon": "fa-solid fa-vitamins"
                },
                {
                    "title": "Energy Boost",
                    "desc": "Boost Energy and Reduce Fatigue",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Immune Support",
                    "desc": "Improve your body's natural defense system",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Mental Clarity",
                    "desc": "Improve mental clarity and concentration",
                    "icon": "fa-solid fa-lightbulb"
                },
                {
                    "title": "Skin Health",
                    "desc": "Enhancing skin health and supporting wound healing",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit from Multivitamin IV Therapy?",
            "subheading": "",
            "para": "Although largely safe, some people will benefit more from the treatment than others:",
            "items": [
                {
                    "title": "Chronic Fatigue Sufferers",
                    "desc": "The energy-boosting vitamins can help those suffering from low energy or chronic fatigue syndromes significantly.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Nutrient Absorption Issues",
                    "desc": "Some people can't absorb nutrients — because of digestive problems or other reasons — and IV therapy means your body gets the full dose.",
                    "icon": "fa-solid fa-stomach"
                },
                {
                    "title": "Frequent Travelers",
                    "desc": "Athletes, frequent travelers, and people exposed to grueling environments use IV therapy to fight jet lag and immune issues.",
                    "icon": "fa-solid fa-plane"
                },
                {
                    "title": "Athletes",
                    "desc": "IV drips provide enhanced nutrition and hydration for high-performance athletes seeking faster recovery and optimal performance.",
                    "icon": "fa-solid fa-running"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation Before Your IV Drip Session",
            "subheading": "",
            "para": "What to Do Before Your Multivitamin IV Drip Appointment at Elite Body Home Polyclinic:",
            "items": [
                {
                    "title": "Vital Signs Monitoring",
                    "desc": "Our healthcare team will take your blood pressure, heart rate and other vital signs at the start of your consultation.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Medical History Review",
                    "desc": "Our trained healthcare professionals will discuss your detailed medical history to create the most suitable IV drip for your needs.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Hydration",
                    "desc": "Hydrate yourself well before your session to ensure easier access to a vein and to improve treatment efficacy.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Light Meal",
                    "desc": "A light meal is recommended before the procedure to balance your blood sugar levels throughout the treatment.",
                    "icon": "fa-solid fa-utensils"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "",
            "para": "Results of multivitamin IV drips can be highly subjective and depend on both the individual's health history and the blend itself:",
            "items": [
                {
                    "title": "Immediate Effects",
                    "desc": "Most clients describe revitalization, heightened energy levels, and mental clarity soon after the treatment.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Improved Immunity",
                    "desc": "The increased vitamins and minerals over time can improve your immune systems ability to fight off sickness.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "Enhanced Skin Health",
                    "desc": "Vitamins with antioxidant properties such as Vitamin C can help improve skin tone, minimize wrinkles, and promote faster wound healing.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Recommendations",
            "subheading": "",
            "para": "To maximize the benefits of your IV therapy session:",
            "items": [
                {
                    "title": "Continue Hydration",
                    "desc": "Drink plenty of water after your session to help your body utilize the nutrients effectively.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Rest",
                    "desc": "Allow your body time to absorb and utilize the nutrients by avoiding strenuous activities immediately after.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Follow-up Sessions",
                    "desc": "Consider regular sessions as recommended by our healthcare professionals for sustained benefits.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does an IV drip session take?",
                    "answer": "A typical session lasts between 30-45 minutes, allowing you to relax while receiving your treatment."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Most people experience no side effects. Some may feel a slight coolness at the injection site or a metallic taste from certain vitamins."
                },
                {
                    "question": "How often should I get IV therapy?",
                    "answer": "Frequency depends on your individual needs. Some benefit from weekly sessions, while others may only need monthly maintenance."
                },
                {
                    "question": "Can I drive after my IV therapy?",
                    "answer": "Yes, IV therapy doesn't impair your ability to drive. Many people feel more energized after their session."
                }
            ]
        },
        // "treatmentAreas1": [],
        // "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/New-Service-Banner-Image-3-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 39,
            "title": "Multivitamin IV Drips in Dubai",
            "description": "Replenish essential vitamins and minerals directly into your bloodstream for immediate absorption and energy boost. Safe and effective treatment with no downtime.",
            "price": 499
        }
    },
    "ar": {
        "url": "multivitamin-iv-drips",
        "meta": {
            "title": "العلاج بالفيتامينات الوريدية في دبي | ايليت بودي هوم",
            "description": "إعادة تغذية جسمك بالفيتامينات والمعادن الأساسية مباشرة في مجرى الدم لامتصاص فوري وزيادة الطاقة. علاج آمن وفعال دون تعافي.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/multivitamin-iv-drips",
            "og": {
                "title": "العلاج بالفيتامينات الوريدية في دبي | ايليت بودي هوم",
                "description": "حقن مباشر للفيتامينات والمعادن الأساسية لفوائد صحية فورية",
                "image": "https://www.elitebodyhome.com/images/multivitamin-iv-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "العلاج بالفيتامينات الوريدية"],
        "hero": {
            "title": "العلاج بالفيتامينات الوريدية في دبي",
            "subtitle": "علاج مباشر بالفيتامينات",
            "description": "قد تؤدي الكثير من الأنماط الحياتية الحديثة والأنظمة الغذائية المتبعة اليوم إلى نقص في المغذيات الدقيقة. تُعد الفيتامينات والمعادن ضرورية لتعزيز الصحة والطاقة، وتدرك عيادات ايليت بودي هوم في دبي هذا الأمر جيدًا، لذلك تتيح لك جلسات العلاج بالفيتامينات الوريدية التي تساعدك على الاسترخاء بينما يتم إعادة تزويد جسمك بالعناصر الغذائية الأساسية."
        },
        "howItWorks": {
            "title": "كيف تعمل جلسات العلاج بالفيتامينات الوريدية؟",
            "description": "نقدم لك في ايليت بودي هوم تركيبة مخصصة من الفيتامينات والمعادن ومضادات الأكسدة الضرورية التي تُحقن مباشرةً في مجرى الدم. وبذلك تتخطى الجهاز الهضمي، مما يتيح لجسمك امتصاص المكونات بشكل كامل وسريع، لتشعر بالفوائد وتحسن الصحة على الفور."
        },
        "procedureSteps": {
            "heading": "المكونات الرئيسية في محاليلنا الوريدية",
            "para": "تحتوي تركيبتنا المتخصصة على العناصر الغذائية الأساسية للصحة المثلى:",
            "steps": [
                {
                    "title": "فيتامين ب المركب",
                    "desc": "يساعد في تحويل العناصر الغذائية الموجودة في الطعام إلى طاقة، وتدعم الوظائف الإدراكية.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "فيتامين سي",
                    "desc": "يُعد فيتامين سي مضاد أكسدة قوي يعزز المناعة ويساعد في إنتاج الكولاجين.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "فيتامين د",
                    "desc": "يعتبر فيتامين هام للحفاظ على كثافة العظام ودعم وظيفة المناعة.",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "الإلكتروليتات",
                    "desc": "تحافظ الإلكتروليتات على ترطيب الجسم وتساعد في دعم وظائف العضلات.",
                    "icon": "fa-solid fa-droplet"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد العلاج بالفيتامينات الوريدية",
            "subheading": "استمتع بتحسينات صحية فورية وطويلة المدى",
            "para": "أهم فوائد العلاج بالفيتامينات الوريدية:",
            "items": [
                {
                    "title": "تعويض نقص المغذيات",
                    "desc": "تعويض نقص الفيتامينات والمعادن الأساسية بشكل فعال.",
                    "icon": "fa-solid fa-vitamins"
                },
                {
                    "title": "زيادة الطاقة",
                    "desc": "تعزيز الطاقة والتقليل من الشعور بالإرهاق.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "دعم المناعة",
                    "desc": "دعم جهاز المناعة وتحسين القدرة على مقاومة الأمراض.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "وضوح ذهني",
                    "desc": "تحسين التركيز والصفاء الذهني.",
                    "icon": "fa-solid fa-lightbulb"
                },
                {
                    "title": "صحة البشرة",
                    "desc": "تعزيز صحة البشرة ودعم التئام الجروح.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "eligibility": {
            "heading": "من يمكنه الاستفادة من العلاج بالفيتامينات الوريدية؟",
            "subheading": "",
            "para": "على الرغم من أن العلاج آمن إلى حد كبير، إلا أن بعض الأشخاص قد يستفيدون منه أكثر من غيرهم:",
            "items": [
                {
                    "title": "مرضى الإرهاق المزمن",
                    "desc": "تساعد الفيتامينات المعزّزة للطاقة من يعانون من الإرهاق الدائم أو متلازمة الإرهاق المزمن.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "مشاكل امتصاص المغذيات",
                    "desc": "لا تمتص بعض الأجسام الفيتامينات بشكل جيد بسبب مشكلات الجهاز الهضمي أو غيره. يوفر العلاج بالتنقيط الوريدي الامتصاص الكامل للمغذيات.",
                    "icon": "fa-solid fa-stomach"
                },
                {
                    "title": "المسافرون الدائمون",
                    "desc": "يُعد هذا العلاج مفيد للرياضيون أو المسافرون بشكل متكرر أو من يتعرضون لبيئات مرهقة، حيث يساعد على مقاومة اضطراب الرحلات الجوية الطويلة ودعم الجهاز المناعي.",
                    "icon": "fa-solid fa-plane"
                },
                {
                    "title": "الرياضيون",
                    "desc": "يوفر العلاج ترطيبًا فوريًا وتغذية داعمة تساعد على تسريع التعافي وتحسين الأداء الرياضي للمحترفون.",
                    "icon": "fa-solid fa-running"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد قبل جلسة التنقيط الوريدي",
            "subheading": "",
            "para": "ما الذي يجب فعله قبل موعد جلسة العلاج بالفيتامينات الوريدية في عيادات ايليت بودي هوم:",
            "items": [
                {
                    "title": "مراقبة العلامات الحيوية",
                    "desc": "يقوم فريقنا الطبي بقياس ضغط الدم، ومعدل ضربات القلب، وغيرها من العلامات الحيوية في بداية الاستشارة.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "مراجعة التاريخ الطبي",
                    "desc": "سيناقش الأطباء المتخصصون لدينا تاريخك الطبي بالتفصيل لتصميم تركيبة التنقيط الوريدي الأكثر ملاءمة لاحتياجاتك.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "يُنصح بشرب كمية كافية من الماء قبل الجلسة لضمان سهولة الوصول إلى الوريد وتحسين فاعلية العلاج.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "وجبة خفيفة",
                    "desc": "يُنصح بتناول وجبة خفيفة قبل الجلسة، للمساعدة في موازنة مستويات السكر في الدم أثناء العلاج.",
                    "icon": "fa-solid fa-utensils"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "تختلف نتائج جلسات التنقيط الوريدي للفيتامينات من شخص لآخر، وتعتمد بشكل كبير على الحالة الصحية للفرد وتركيبة المحلول المستخدمة:",
            "items": [
                {
                    "title": "تأثير فوري",
                    "desc": "يصف معظم العملاء شعورًا بالانتعاش وزيادة في مستويات الطاقة ووضوحًا ذهنيًا بعد الجلسة مباشرة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تعزيز المناعة",
                    "desc": "يساهم ارتفاع مستويات الفيتامينات والمعادن مع مرور الوقت في تعزيز قدرة الجهاز المناعي على مقاومة الأمراض.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "تحسين صحة البشرة",
                    "desc": "تساعد الفيتامينات ذات الخصائص المضادة للأكسدة مثل فيتامين سي على توحيد لون البشرة، وتقليل التجاعيد، وتسريع التئام الجروح.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح ما بعد العلاج",
            "subheading": "",
            "para": "لتحقيق أقصى استفادة من جلسة العلاج الوريدي:",
            "items": [
                {
                    "title": "الاستمرار في الترطيب",
                    "desc": "اشرب الكثير من الماء بعد الجلسة لمساعدة جسمك على استخدام العناصر الغذائية بشكل فعال.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "الراحة",
                    "desc": "امنح جسمك الوقت الكافي لامتصاص العناصر الغذائية عن طريق تجنب الأنشطة الشاقة مباشرة بعد الجلسة.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "جلسات المتابعة",
                    "desc": "فكر في الجلسات المنتظمة حسبما يوصي بها أخصائيو الرعاية الصحية للحصول على فوائد مستدامة.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تستغرق جلسة التنقيط الوريدي؟",
                    "answer": "تستغرق الجلسة النموذجية ما بين 30-45 دقيقة، مما يتيح لك الاسترخاء أثناء تلقي العلاج."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "معظم الناس لا يعانون من أي آثار جانبية. قد يشعر البعض ببرودة طفيفة في موقع الحقن أو طعم معدني من بعض الفيتامينات."
                },
                {
                    "question": "كم مرة يجب أن أحصل على العلاج الوريدي؟",
                    "answer": "يعتمد التكرار على احتياجاتك الفردية. يستفيد البعض من الجلسات الأسبوعية، بينما قد يحتاج الآخرون إلى صيانة شهرية فقط."
                },
                {
                    "question": "هل يمكنني القيادة بعد العلاج الوريدي؟",
                    "answer": "نعم، العلاج الوريدي لا يضعف قدرتك على القيادة. يشعر الكثير من الناس بمزيد من الطاقة بعد جلساتهم."
                }
            ]
        },
        // "treatmentAreas1": [],
        // "treatmentAreas2": []
    }
}